import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Breadcrumb } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes } from '../../auth/permissions';
import { AiTokenBudgetPanel } from './AiTokenBudgetPanel';
import { TokenUsageReport } from './TokenUsageReport';
import styles from './AiUsage.module.css';

type AiUsageTab = 'tokens';

const TABS: Array<{ id: AiUsageTab; labelKey: string }> = [
  { id: 'tokens', labelKey: 'tokenUsageTab' },
];

const DEFAULT_TAB: AiUsageTab = 'tokens';

const resolveTab = (value: string | null): AiUsageTab => (
  TABS.some((tab) => tab.id === value) ? (value as AiUsageTab) : DEFAULT_TAB
);

export const AiUsagePage: React.FC = () => {
  const { t } = useTranslation(['aiUsage', 'nav']);
  const { hasPermission } = useAuth();
  const canManageTokenBudget = hasPermission(PermissionCodes.adminAccess);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = resolveTab(searchParams.get('tab'));

  const changeTab = (tab: AiUsageTab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className={`company-admin-page ${styles.page}`}>
      <Breadcrumb items={[{ label: t('title'), path: '/ai-usage' }]} />

      <div className="company-tabs" role="tablist" aria-label={t('title')}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`company-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => changeTab(tab.id)}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      <div className="company-tab-panel">
        {activeTab === 'tokens' ? (
          <>
            {canManageTokenBudget ? <AiTokenBudgetPanel /> : null}
            <TokenUsageReport />
          </>
        ) : null}
      </div>
    </div>
  );
};
